import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle";
import * as styled from "./MyStudents.styled";
import axios from "../../../axios";
import { fetchAuthMe } from "../../../redux/slices/auth";
import { PagesWrapper } from '../../../styles/components/PagesWrapper.styled'
import { courseOf, fetchPlatformCourses } from "../../../helpers/materials/platformLessons";
import { isPlatformOnly, isSchoolLang } from "../../../helpers/langs";
import MaterialsSourceSwitch from "../../../components/MaterialsSourceSwitch/MaterialsSourceSwitch";
import { LuPencil, LuCheck, LuX } from "react-icons/lu";

const groupNames = { kids: "Діти", teenagers: "Підлітки", adults: "Дорослі" };
const levelName = (level) => (level || "—").replace(/\b\w/g, (letter) => letter.toUpperCase());

function MyStudents() {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [levels, setLevels] = useState({});
  const [editingStudent, setEditingStudent] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState("");
  // Курси, для яких уже є уроки платформи (adults-beginner, …) — лише туди можна перевести учня
  const [platformCourses, setPlatformCourses] = useState([]);
  const [savingSource, setSavingSource] = useState(null);

  useEffect(() => {
    fetchPlatformCourses().then(setPlatformCourses);
  }, []);

  useEffect(() => {
    if (data) {
      data.students.forEach((student) => {
        fetchLevels(student.ageGroup, data.accessType);
      });
    }
  }, [data]);

  useEffect(() => {
    const authMe = async () => {
      const data = await dispatch(fetchAuthMe());

      return data;
    };
    authMe().then((res) => setData(res.payload));
  }, [dispatch]);

  const fetchLevels = async (ageGroup, type) => {
    try {
      const response = await axios.get(`/levels/${ageGroup}/${type}`);

      setLevels((prev) => ({ ...prev, [ageGroup]: response.data.levels }));
    } catch (error) {
      console.error("Failed to fetch levels", error);
    }
  };

  const handleEditClick = (studentId, currentLevel) => {
    setEditingStudent(studentId);
    setSelectedLevel(currentLevel);
  };

  const handleLevelChange = (e) => {
    setSelectedLevel(e.target.value);
  };

  const handleSaveClick = async (studentId, teacherId) => {
    try {
      
      await axios.patch(`/students/${studentId}/${teacherId}`, {
        englishLevel: selectedLevel,
      });
      setEditingStudent(null);
      const updatedData = await dispatch(fetchAuthMe());
      setData(updatedData.payload);
    } catch (error) {
      console.error("Failed to update level", error);
    }
  };

  const handleSourceChange = async (studentId, materialsSource) => {
    setSavingSource(studentId);
    try {
      await axios.patch(`/students/${studentId}/${data._id}`, { materialsSource });
      const updatedData = await dispatch(fetchAuthMe());
      setData(updatedData.payload);
    } catch (error) {
      console.error("Failed to update materials source", error);
    } finally {
      setSavingSource(null);
    }
  };

  if (!data) return null;

  const showSource = isSchoolLang(data.accessType) && !isPlatformOnly(data.accessType);

  return (
    <PagesWrapper>
      <TopPageTitle value={"Мої учні"} />
      <styled.Summary>
        {data.students.length ? `${data.students.length} ${plural(data.students.length)}` : "Поки немає учнів"}
      </styled.Summary>
      <styled.StudentsList>
        {data.students.map(
          ({ name, studentId, ageGroup, englishLevel, _id, materialsSource }, index) => {
            const hasPlatform = isSchoolLang(data.accessType) && platformCourses.includes(courseOf(ageGroup, englishLevel, data.accessType));
            const editing = editingStudent === _id;
            return (
            <styled.StudentItem key={studentId}>
              <styled.Head>
                <styled.StudentAvatar $tone={index % 4}>{(name || "?").trim().charAt(0).toUpperCase()}</styled.StudentAvatar>
                <styled.StudentDetails>
                  <styled.StudentName>{name}</styled.StudentName>
                  <styled.Chips>
                    <styled.Chip>ID {studentId}</styled.Chip>
                    <styled.Chip $soft>{groupNames[ageGroup] || ageGroup}</styled.Chip>
                  </styled.Chips>
                </styled.StudentDetails>
              </styled.Head>

              <styled.Row>
                <styled.RowLabel>Рівень</styled.RowLabel>
                {editing ? (
                  <styled.EditBox>
                    <styled.Select value={selectedLevel} onChange={handleLevelChange}>
                      {levels[ageGroup]?.map((level) => (
                        <option key={level} value={level}>
                          {levelName(level)}
                        </option>
                      ))}
                    </styled.Select>
                    <styled.IconButton $primary title="Зберегти" onClick={() => handleSaveClick(_id, data._id)}>
                      <LuCheck size={16} />
                    </styled.IconButton>
                    <styled.IconButton title="Скасувати" onClick={() => setEditingStudent(null)}>
                      <LuX size={16} />
                    </styled.IconButton>
                  </styled.EditBox>
                ) : (
                  <styled.LevelValue>
                    {levelName(englishLevel)}
                    <styled.IconButton title="Змінити рівень" onClick={() => handleEditClick(_id, englishLevel)}>
                      <LuPencil size={14} />
                    </styled.IconButton>
                  </styled.LevelValue>
                )}
              </styled.Row>

              {showSource && (
                <styled.SourceRow>
                  <MaterialsSourceSwitch
                    value={materialsSource}
                    hasPlatform={hasPlatform}
                    showHint
                    disabled={savingSource === _id}
                    onChange={(value) => handleSourceChange(_id, value)}
                  />
                </styled.SourceRow>
              )}
            </styled.StudentItem>
            );
          }
        )}
      </styled.StudentsList>
    </PagesWrapper>
  );
}

const plural = (n) => (n % 10 === 1 && n % 100 !== 11 ? "учень" : [2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100) ? "учні" : "учнів");

export default MyStudents;
